import { ImportProgress } from "./ImportProgress";
import { useEffect, useState } from "react";
import { getPublicReporting, type PublicReporting } from "./communityReportingApi";
import { useReportingCopy, type ReportingCopyKey } from "./helpdeskReportingCopy";
import { activeDateTimeFormatter, activeFormattingLocale, formatActiveNumber } from "./i18n/formatting";
import "./styles/community-reporting.css";

const dimensions = ["requests", "attacks", "responses", "platforms", "communities", "months"] as const;
export function CommunityThreatAnalytics() {
  const { t } = useReportingCopy();
  const [data, setData] = useState<PublicReporting | null>(null);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [dimension, setDimension] = useState<(typeof dimensions)[number]>("requests");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setError(false); setData(null);
    void getPublicReporting(controller.signal).then(setData).catch(() => {
      if (!controller.signal.aborted) setError(true);
    });
    return () => controller.abort();
  }, [revision]);
  const rows = (data?.snapshot?.dimensions[dimension] ?? []).filter(row => row.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const maximum = Math.max(1, ...rows.map(row => row.upper));
  return <main className="analytics-page community-reporting">
    <section className="analytics-intro shell"><div><span className="eyebrow">KAVOOSH PUBLIC INTELLIGENCE</span><h1>{t("communityTitle")}</h1><p>{t("intro")}</p></div><button className="button secondary" disabled={!data && !error} onClick={() => setRevision(v => v + 1)}>{t("retry")}</button></section>
    <div className="shell">
      <p className="community-reporting-notice">{t("boundary")}</p>
      {error ? <p role="alert">{t("unavailable")}</p> : !data ? <ImportProgress label={t("loading")} /> : data.state !== "published" ? <p role="status">{t(data.state === "withdrawn" ? "withdrawn" : "empty")}</p> : <>
        <p>{t("publishedLabel")} · <bdi dir="ltr">{data.published_at ? activeDateTimeFormatter(activeFormattingLocale(), { dateStyle: "medium", timeStyle: "short" }).format(new Date(data.published_at)) : "—"}</bdi></p>
        <section className="community-reporting-surface">
          <div className="community-reporting-filters"><label>{t("dimension")}<select value={dimension} onChange={e => { setDimension(e.target.value as typeof dimension); setQuery(""); }}>{dimensions.map(value => <option key={value} value={value}>{t(value)}</option>)}</select></label><label>{t("search")}<input type="search" value={query} onChange={e => setQuery(e.target.value)} /></label></div>
          <h2>{t(dimension as ReportingCopyKey)}</h2>
          {rows.length ? <table><thead><tr><th>{t("dimension")}</th><th>{t("range")}</th></tr></thead><tbody>{rows.map(row => <tr key={row.label}><th scope="row">{row.label}</th><td><span className="community-reporting-bar" aria-hidden="true" style={{ width: `${(row.lower / maximum) * 65}%` }} /><bdi>{formatActiveNumber(row.lower)}–{formatActiveNumber(row.upper)}</bdi></td></tr>)}</tbody></table> : <p>{t("noBuckets")}</p>}
        </section>
      </>}
      <p className="community-reporting-notice">{t("privacy")}</p><p>{t("method")}</p>
    </div>
  </main>;
}
